import { fetchEvents, fetchCategories, fetchEventsForMap, fetchEventDetails } from '../../services/strapiService';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents, Circle } from 'react-leaflet';
import { Icon } from 'leaflet';
import styled, { createGlobalStyle } from 'styled-components';
import FilterPanel from './FilterPanel';
import EventSidebar from './EventSidebar';
import { useNavigate } from 'react-router-dom';
import useWindowSize from '../../hooks/useWindowSize';
import InfoBox from './InfoBox'; 
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import L from 'leaflet';
import AdBanner from '../common/AdBanner';
import { COLORS } from '../../styles/Colors';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import { 
  trackEventView, 
  trackSearch, 
  trackMapInteraction, 
  trackFilterUsage,
  trackLocationRequest,
  trackNearbyEventsView,
  trackOverlappingEventsPopup,
  trackDonationClick
} from '../../utils/analytics';
import emailjs from '@emailjs/browser';
import { getMacroCategoryForCategory } from '../../utils/categoryUtils';

// ========== CONFIGURAZIONE CATEGORIE ==========
const CATEGORY_STYLES = {
  'spettacolo': {
    color: '#e74c3c', // Rosso
    icon: 'fas fa-theater-masks',
    markerIcon: '/marker-icon-red.png',
    markerIconSelected: '/marker-icon-red-selected.png',
    categoryIds: [16, 2, 18, 17, 1, 3, 27, 26, 25, 24, 23, 22, 28, 29, 32], // Ballo, Cinema, Classica, Concerti, Musica, Teatro
    name: 'Spettacolo'
  },
  'culinaria': {
    color: '#f39c12', // Arancione 
    icon: 'fas fa-utensils',
    markerIcon: '/marker-icon-orange.png',
    markerIconSelected: '/marker-icon-orange-selected.png',
    categoryIds: [4, 9], // Gastronomia, Sagre
    name: 'Enogastronomia'
  },
  'sport': {
    color: '#27ae60', // Verde
    icon: 'fas fa-running',
    markerIcon: '/marker-icon-green.png',
    markerIconSelected: '/marker-icon-green-selected.png',
    categoryIds: [20, 7, 21], // Corsa, Sport, Trekking/corsa
    name: 'Sport & Movimento'
  },
  'cultura': {
    color: '#9b59b6', // Viola
    icon: 'fas fa-university',
    markerIcon: '/marker-icon-purple.png',
    markerIconSelected: '/marker-icon-purple-selected.png',
    categoryIds: [10, 5, 15, 19, 34], // Cultura, Mostre, Religione, Tradizioni, Manifestazioni
    name: 'Arte & Cultura'
  },
  'Free Palestine': {
    color: '#ff0000ff', // rosso
    icon: 'fas fa-calendar-alt',
    markerIcon: '/free_palestine_flag.png',
    markerIconSelected: '/marker-icon-gray-selected.png',
    categoryIds: [35],
    name: 'Free Palestine'
  },
  'eventi': {
    color: '#e91e63', // Rosa
    icon: 'fas fa-calendar-star',
    markerIcon: '/marker-icon-pink.png',
    markerIconSelected: '/marker-icon-pink-selected.png',
    categoryIds: [8], // Festival
    name: 'Festival & Eventi'
  },
  'natura': {
    color: '#2ecc71', // Verde scuro
    icon: 'fas fa-leaf',
    markerIcon: '/marker-icon-dark-green.png',
    markerIconSelected: '/marker-icon-dark-green-selected.png',
    categoryIds: [11], // Natura
    name: 'Natura & Outdoor'
  },
  'lifestyle': {
    color: '#f1c40f', // Giallo
    icon: 'fas fa-star',
    markerIcon: '/marker-icon-yellow.png',
    markerIconSelected: '/marker-icon-yellow-selected.png',
    categoryIds: [14], // Moda, Workshop
    name: 'Lifestyle & Formazione'
  },
  'Tecnologia': {
    color: '#34495e', // Grigio scuro
    icon: 'fas fa-laptop',
    markerIcon: '/marker-icon-dark.png',
    markerIconSelected: '/marker-icon-dark-selected.png',
    categoryIds: [33], // Tecnologia, Virtuali
    name: 'Digitale & Tech'
  },
  'default': {
    color: '#95a5a6', // Grigio
    icon: 'fas fa-calendar-alt',
    markerIcon: '/marker-icon-gray.png',
    markerIconSelected: '/marker-icon-gray-selected.png',
    categoryIds: [],
    name: 'Altri Eventi'
  }
};

// ========== FUNZIONI UTILITY CATEGORIE ==========
const getMacroCategory = (eventCategories) => {
  if (!eventCategories || eventCategories.length === 0) {
    return 'default';
  }
  
  // Cerca nelle categorie dell'evento quale macro-categoria corrisponde
  for (const [macroKey, config] of Object.entries(CATEGORY_STYLES)) {
    if (macroKey === 'default') continue;
    
    const hasMatchingCategory = eventCategories.some(eventCat => 
      config.categoryIds.includes(eventCat.id)
    );
    
    if (hasMatchingCategory) {
      return macroKey;
    }
  }
  
  return 'default';
};

// Funzione per creare icone dinamiche basate sulla categoria
const createCategoryIcon = (eventCategories, isSelected = false) => {
  const macroCategory = getMacroCategory(eventCategories);
  const config = CATEGORY_STYLES[macroCategory];
  
  const iconUrl = isSelected ? 
    (config.markerIconSelected || config.markerIcon) : 
    config.markerIcon;
  
  // Aumenta le dimensioni per eventi selezionati
  const size = isSelected ? [50, 40] : [40, 30];
  const anchor = isSelected ? [25, 40] : [20, 30];
  
  return new Icon({
    iconUrl: iconUrl,
    iconSize: size,
    iconAnchor: anchor,
    popupAnchor: [1, -34],
    // Aggiungi una classe CSS per ulteriori personalizzazioni
    className: isSelected ? 'selected-marker' : 'normal-marker'
  });
};

// ========== STYLED COMPONENTS ==========

// ✅ NUOVO: Styled Components per i filtri attivi
const ActiveFiltersContainer = styled.div`
  position: absolute;
  top: 80px; // Sotto il logo
  left: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 400px;
  z-index: 999;
  
  @media (max-width: 768px) {
    top: 70px;
    left: 15px;
    max-width: calc(100vw - 120px); // Lascia spazio per i pulsanti mobile
  }
`;

const FilterTag = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid #e9ecef;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #495057;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  max-width: 200px;
  
  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 5px 10px;
    max-width: 150px;
  }
`;

const FilterTagIcon = styled.i`
  margin-right: 6px;
  color: ${props => props.color || COLORS.primary || '#f39c12'};
  font-size: 10px;
  
  @media (max-width: 768px) {
    font-size: 9px;
    margin-right: 4px;
  }
`;

const FilterTagText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const FilterTagRemove = styled.button`
  background: none;
  border: none;
  color: #6c757d;
  font-size: 14px;
  margin-left: 8px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  border-radius: 50%;
    &:hover {
    color: #dc3545;
    background-color: rgba(220, 53, 69, 0.1);
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
    margin-left: 6px;
  }
`;

const ClearAllFiltersButton = styled.button`
  display: flex;
  align-items: center;
  background: rgba(220, 53, 69, 0.9);
  color: white;
  border: none;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
  
  &:hover {
    background: rgba(220, 53, 69, 1);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
  }
  
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 5px 10px;
  }
`;


const MapWrapper = styled.div`
  height: 100vh;
  width: 100%;
  position: relative;
  /* Rimuovi il padding-bottom dinamico */
  
  @media (max-width: 768px) {
    /* Nessun padding aggiuntivo */
  }
`;

const TopControlsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  z-index: 1000;
  background: linear-gradient(to bottom, 
                             rgba(255, 255, 255, 0.9) 0%, 
                             rgba(255, 255, 255, 0.8) 50%, 
                             rgba(255, 255, 255, 0.3) 80%, 
                             rgba(255, 255, 255, 0) 100%);
  padding: 10px 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;
  
  @media (max-width: 768px) {
    top: env(safe-area-inset-top, 0);
    padding-top: calc(10px + env(safe-area-inset-top, 0));
    height: auto;
    min-height: 60px;
    padding-bottom: 15px;
    /* Rimuovi il padding-right eccessivo */
    padding-right: 15px;
  }
`;

// Nuovo styled component per il gruppo di controlli
const ControlsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: auto;
  
  @media (max-width: 768px) {
    gap: 6px;
    /* Assicura che il gruppo mantenga la sua larghezza */
    min-width: fit-content;
    flex-shrink: 0;
  }
`;

// Modifica il pulsante per uniformarlo agli altri
const UniformButton = styled.button`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.2s ease;
  pointer-events: auto;
  color: #f39c12; /* Colore giallo-arancione per le icone */
  
  &:hover {
    background: #f5f5f5;
    transform: scale(1.05);
    color: #e67e22; /* Colore leggermente più scuro al hover */
  }
  
  &:active {
    color: #d35400; /* Colore ancora più scuro quando premuto */
  }
  
  @media (max-width: 768px) {
    width: 42px;
    height: 42px;
    font-size: 18px;
    border: 2px solid #e0e0e0;
    color: #f39c12; /* Stesso colore su mobile */
    
    &:hover {
      color: #e67e22;
    }
  }
`;


const LogoOverlay = styled.div`
  margin-right: 20px;
  pointer-events: auto;
  flex-shrink: 0; /* Impedisce al logo di ridursi */
  
  .desktop-logo {
    height: 40px;
    display: block;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  .mobile-logo {
    height: 40px;
    display: none;
    
    @media (max-width: 768px) {
      display: block;
      height: 35px; /* Leggermente più piccolo su mobile per risparmiare spazio */
    }
  }
`;

const SearchBar = styled.div`
  display: flex;
  pointer-events: auto;
  position: relative;
  
  form {
    display: flex;
    position: relative;
  }
  
  input {
    padding: 8px 12px;
    border-radius: 20px;
    border: 1px solid #e0e0e0;
    width: 250px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.08);
    font-size: 14px;
    
    @media (max-width: 768px) {
      width: ${props => props.isSearchOpen ? 'calc(100vw - 200px)' : '0'}; /* Cambiato da 120px a 200px per lasciare spazio ai pulsanti */
      padding: ${props => props.isSearchOpen ? '8px 12px' : '0'};
      border: ${props => props.isSearchOpen ? '1px solid #e0e0e0' : 'none'};
      opacity: ${props => props.isSearchOpen ? '1' : '0'};
      transition: all 0.3s ease;
      max-width: 180px; /* Aggiungi una larghezza massima */
    }
  }
  
  button {
    margin-left: 5px;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0,0,0,0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #f39c12;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f5f5f5;
      color: #e67e22;
    }
    
    &:active {
      color: #d35400;
    }
  }
  @media (max-width: 768px) {
    /* Su mobile, nascondi il pulsante di ricerca del form quando si usa il pulsante mobile */
    button {
      display: none;
    }
  }
`;

const MobileSearchButton = styled.button`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 2px solid #e0e0e0;
    border-radius: 50%;
    width: 42px;
    height: 42px;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0,0,0,0.08);
    font-size: 18px;
    z-index: 1001;
    pointer-events: auto;
    transition: all 0.2s ease;
    color: #f39c12; /* Colore giallo-arancione */
    
    &:hover {
      background: #f5f5f5;
      transform: scale(1.05);
      color: #e67e22; /* Colore più scuro al hover */
    }
    
    &:active {
      color: #d35400;
    }
  }
`;


const NoResultsMessage = styled.div`
  position: absolute;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  padding: 10px 20px;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  z-index: 1000;
  font-size: 14px;
  display: flex;
  align-items: center;
  
  button {
    background: none;
    border: none;
    color: #f39c12;
    margin-left: 10px;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
  }
`;

const SearchSuggestions = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 250px;
  background: white;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1001;
  margin-top: 5px;
  
  @media (max-width: 768px) {
    width: calc(100vw - 200px); /* Stessa larghezza dell'input */
    max-width: 180px; /* Stessa max-width dell'input */
  }
  
  div {
    padding: 8px 12px;
    cursor: pointer;
    border-bottom: 1px solid #f1f1f1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
    &:hover {
      background: #f9f9f9;
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

// Nuovo componente per l'header con pulsante collapse
const NearbyEventsHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px; /* Ridotto da 12px */
  
  @media (max-width: 768px) {
    margin-bottom: 6px;
  }
`;



const CollapseButton = styled.button`
  background: ${COLORS.primary || '#f39c12'};
  color: white;
  border: none;
  border-radius: 6px; /* Più piccolo */
  width: 28px; /* Ridotto da 36px */
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px; /* Più piccolo */
  transition: all 0.2s ease;
  
  &:hover {
    background: ${COLORS.primaryDark || '#e67e22'};
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    width: 24px; /* Ancora più piccolo */
    height: 24px;
    font-size: 10px;
    border-radius: 4px;
  }
`;

// Stili per eventi vicini
const NearbyEventsContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 15px;
  right: 15px;
  z-index: 1000; /* Appena sopra l'AdBanner */
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 8px 12px;
  pointer-events: auto;
  transition: transform 0.3s ease;
  transform: ${props => props.isCollapsed ? 'translateY(calc(100% - 40px))' : 'translateY(0)'};
  
  @media (max-width: 768px) {
    bottom: 20px; /* 60px (altezza AdBanner) + 20px di margine */
    left: 10px;
    right: 10px;
    padding: 6px 10px;
    border-radius: 10px;
    transform: ${props => props.isCollapsed ? 'translateY(calc(100% - 36px))' : 'translateY(0)'};
    z-index: 1001;
    
    /* Assicurati che tutti gli elementi siano cliccabili */
    * {
      pointer-events: auto;
    }
  }
`;



const NearbyEventsScroll = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 8px;
  padding: 2px 0;
  
  /* Su desktop, mostra una scrollbar sottile e stilizzata */
  @media (min-width: 769px) {
    scrollbar-width: thin;
    scrollbar-color: ${COLORS.primary || '#f39c12'} transparent;
    
    &::-webkit-scrollbar {
      height: 6px;
      display: block;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${COLORS.primary || '#f39c12'};
      border-radius: 3px;
      
      &:hover {
        background: ${COLORS.primaryDark || '#e67e22'};
      }
    }
  }
  
  /* Su mobile, nascondi scrollbar ma mantieni funzionalità touch */
  @media (max-width: 768px) {
    scrollbar-width: none;
    -ms-overflow-style: none;
    
    &::-webkit-scrollbar {
      display: none;
    }
  }
  
  /* Scroll smooth su entrambe le piattaforme */
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
`;

const NearbyEventCard = styled.div`
  min-width: 160px; /* Ridotto da 200px */
  width: 160px;
  height: 90px; /* Ridotto da 120px */
  background: white;
  border-radius: 8px; /* Ridotto da 12px */
  box-shadow: 0 1px 6px rgba(0,0,0,0.08); /* Ombra più sottile */
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(0,0,0,0.06);
  
  &:hover {
    transform: translateY(-2px); /* Ridotto da -3px */
    box-shadow: 0 4px 12px rgba(0,0,0,0.12); /* Ombra hover più sottile */
  }
  
  @media (max-width: 768px) {
    min-width: 140px; /* Ridotto da 180px */
    width: 140px;
    height: 80px; /* Ridotto da 100px */
    border-radius: 6px;
  }
`;

const ScrollContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    /* Su mobile non mostrare i pulsanti freccia */
    .scroll-button {
      display: none;
    }
  }
`;

const ScrollButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  color: ${COLORS.primary || '#f39c12'};
  transition: all 0.2s ease;
  z-index: 1;
  flex-shrink: 0;
  
  &:hover {
    background: white;
    transform: scale(1.1);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NearbyEventsScrollWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  margin: 0 8px;
  
  @media (max-width: 768px) {
    margin: 0;
  }
`;

const CardImage = styled.div`
  width: 100%;
  height: 65%; /* Leggermente ridotto */
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f8f8f8;
  position: relative;
  
  ${props => !props.src && `
    display: flex;
    align-items: center;
    justify-content: center;
    color: #bbb;
    font-size: 18px; /* Ridotto da 24px */
    background: linear-gradient(135deg, #f8f8f8 0%, #eeeeee 100%);
    
    @media (max-width: 768px) {
      font-size: 16px; /* Ridotto da 20px */
    }
  `}
  
  transition: transform 0.2s ease;
  
  &:hover {
    transform: ${props => props.src ? 'scale(1.02)' : 'none'};
  }
`;

const CardOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.75));
  color: white;
  padding: 4px 6px; /* Ridotto da 8px */
  font-size: 10px; /* Ridotto da 12px */
  font-weight: 600;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 9px; /* Ridotto da 11px */
    padding: 3px 5px;
  }
`;

const CardInfo = styled.div`
  height: 35%;
  padding: 4px 6px; /* Ridotto da 6px 8px */
  display: flex;
  justify-content: center;
  align-items: center;
  background: white;
  
  @media (max-width: 768px) {
    padding: 3px 5px;
  }
`;

const DistanceText = styled.span`
  color: ${COLORS.primary || '#f39c12'};
  font-weight: bold;
  font-size: 10px; /* Ridotto da 12px */
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 9px; /* Ridotto da 11px */
  }
`;

const NearbyEventsHeader = styled.div`
  color: #333;
  font-size: 14px; /* Più piccolo */
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 12px; /* Ancora più piccolo su mobile */
  }
`;

// Wrapper per il contenuto scrollabile
const NearbyEventsContent = styled.div`
  opacity: ${props => props.isCollapsed ? '0' : '1'};
  visibility: ${props => props.isCollapsed ? 'hidden' : 'visible'};
  max-height: ${props => props.isCollapsed ? '0' : '120px'}; /* Ridotto da 160px */
  overflow: hidden;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    max-height: ${props => props.isCollapsed ? '0' : '100px'}; /* Ridotto da 140px */
  }
`;

// Aggiungi questo dopo gli altri styled components nell'EventMap
const DonationButton = styled.a`
  position: fixed;
  top: 80px; /* Posizionato sotto il menu (che ha height 60px + padding) */
  right: 20px;
  background: linear-gradient(135deg, #ff6b35, #f7931e);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 18px;
  font-size: 14px;
  font-weight: bold;
  text-decoration: none;
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.4);
  z-index: 999;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  max-width: 200px;
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 6px 20px rgba(255, 107, 53, 0.6);
    background: linear-gradient(135deg, #ff5722, #e68900);
  }
  
  &:active {
    transform: translateY(0) scale(0.98);
  }
  
  .coffee-icon {
    font-size: 16px;
    animation: bounce 2s infinite;
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-3px);
    }
    60% {
      transform: translateY(-2px);
    }
  }
  
  @media (max-width: 768px) {
    top: calc(80px + env(safe-area-inset-top, 0)); /* Considera la safe area su mobile */
    right: 15px;
    padding: 10px 14px;
    font-size: 12px;
    border-radius: 25px;
    z-index: 999;
    
    .donation-text {
      display: none;
    }
        
    .desktop-text {
      display: none;
    }
  }
  
  @media (min-width: 769px) {
        
    .desktop-text {
      display: inline;
    }
  }
`;

// Pulsante per suggerire eventi
const SuggestEventButton = styled.a`
  position: fixed;
  top: 140px; /* Posizionato sotto il pulsante donazione */
  right: 20px;
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 18px;
  font-size: 14px;
  font-weight: bold;
  text-decoration: none;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4);
  z-index: 999;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  max-width: 200px;
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 6px 20px rgba(40, 167, 69, 0.6);
    background: linear-gradient(135deg, #218838, #1ba085);
  }
  
  &:active {
    transform: translateY(0) scale(0.98);
  }
  
  .suggest-icon {
    font-size: 16px;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
  
  @media (max-width: 768px) {
    top: calc(140px + env(safe-area-inset-top, 0));
    right: 15px;
    padding: 10px 14px;
    font-size: 12px;
    border-radius: 25px;
    
    .suggest-text {
      display: none;
    }
    
    .desktop-text {
      display: none;
    }
  }
  
  @media (min-width: 769px) {
    .desktop-text {
      display: inline;
    }
  }
`;

// Popup per suggerire evento
const SuggestEventPopup = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 20px;
`;

const SuggestEventModal = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-width: 95vw;
    border-radius: 8px;
  }
`;

const SuggestEventHeader = styled.div`
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  padding: 16px 20px;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
  
    @media (max-width: 768px) {
    border-radius: 8px 8px 0 0;
    padding: 14px 16px;
    
    h3 {
      font-size: 16px;
    }
  }
`;

const SuggestEventCloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.05);
  }
`;

const SuggestEventForm = styled.form`
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  
  label {
    display: block;
    margin-bottom: 6px;
    font-weight: 600;
    color: #333;
    font-size: 14px;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 10px 12px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    transition: border-color 0.2s ease;
    box-sizing: border-box;
    
    &:focus {
      outline: none;
      border-color: #28a745;
      box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
    }
    
    &::placeholder {
      color: #999;
    }
  }
  
  textarea {
    min-height: 80px;
    resize: vertical;
    font-family: inherit;
  }
  
  select {
    cursor: pointer;
    background-color: white;
  }
`;

const DateTimeGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #218838, #1ba085);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  
  &:hover {
    background: #5a6268;
    transform: translateY(-1px);
  }
`;

const FormNote = styled.p`
  font-size: 12px;
  color: #666;
  margin: 16px 0 0 0;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #28a745;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;


// Aggiungi questi styled components dopo gli altri

const OverlappingEventsPopup = styled.div`
  position: fixed;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  z-index: 1100;
  max-width: 320px;
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  
  /* Posizionamento dinamico */
  left: ${props => props.position.x}px;
  top: ${props => props.position.y}px;
  
  /* Responsive */
  @media (max-width: 768px) {
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    max-width: 90vw;
    max-height: 70vh;
  }
`;

const PopupHeader = styled.div`
  background: ${COLORS.primary || '#f39c12'};
  color: white;
  padding: 12px 16px;
  border-radius: 12px 12px 0 0;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px; /* Spazio tra il testo e il pulsante */

  position: sticky; /* Rende l'header fisso durante lo scroll */
  top: 0; /* Posizione in cima al contenitore */
  z-index: 10; /* Assicura che sia sopra gli altri elementi */

  /* Assicura che il testo non spinga via il pulsante */
  span:first-child {
    flex: 1;
    min-width: 0; /* Permette al testo di essere troncato se necessario */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;



const PopupCloseButton = styled.button`
  background: rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
  min-width: 32px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;


const EventListItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const EventItemTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
`;

const EventItemDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

const EventItemCategory = styled.span`
  background: ${props => props.color || '#f39c12'};
  color: white;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
`;

const EventItemDate = styled.span`
  color: #888;
  font-size: 11px;
`;

// Aggiungi questi styled components dopo gli altri

const MonthSection = styled.div`
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const MonthHeader = styled.div`
  background: #f8f9fa;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e9ecef;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const EventItemTime = styled.span`
  color: #6c757d;
  font-size: 10px;
`;

// Aggiungi questo styled component dopo gli altri

const EventCounter = styled.div`
  position: fixed;
  bottom: 20px; /* Stesso bottom del NearbyEventsContainer */
  left: 50%;
  transform: translateX(-50%); /* Centra orizzontalmente */
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  z-index: 1002; /* Più alto del NearbyEventsContainer (1001) per stare sopra */
  font-size: 14px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  pointer-events: none;
  transition: all 0.3s ease;
  
  /* Aggiungi queste righe per gestire la visibilità */
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  
  .count-number {
    color: ${COLORS.primary || '#f39c12'};
    font-weight: 700;
  }
  
  .filter-indicator {
    background: ${COLORS.primary || '#f39c12'};
    color: white;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 500;
  }
  
  @media (max-width: 768px) {
    bottom: 20px; /* Stesso bottom del NearbyEventsContainer anche su mobile */
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    text-align: center;
    font-size: 12px;
    padding: 6px 12px;
    max-width: calc(100vw - 30px);
    z-index: 1002; /* Sempre sopra il banner eventi vicini */
  }
`;




// ========== ICONE E STILI GLOBALI ==========

// Marker personalizzato per la posizione utente
const userLocationIcon = new Icon({
  iconUrl: '/user-location-marker.png',
  iconSize: [40, 30],
  iconAnchor: [20, 30],
  popupAnchor: [1, -34],
});



const ClusterStyles = createGlobalStyle`
  .custom-cluster {
    background: none !important;
    border: none !important;
  }
  
  .cluster-marker {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background-color: ${COLORS.primary || '#f39c12'};
    color: white;
    font-weight: bold;
    border-radius: 50%;
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.5);
    font-size: 14px;
  }
  
  /* Stili per marker selezionati */
  .selected-marker {
    filter: drop-shadow(0 0 10px rgba(243, 156, 18, 0.8));
    z-index: 1000 !important;
    transition: all 0.3s ease;
  }
  
  .normal-marker {
    transition: all 0.3s ease;
  }
  
  /* Stili per marker multipli */
  .multiple-events-marker {
    transition: all 0.3s ease;
  }
  
  .multiple-events-marker.selected {
    filter: drop-shadow(0 0 10px rgba(243, 156, 18, 0.8));
    animation: pulse 2s infinite;
  }
  
  /* Animazione per marker selezionati */
  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      transform: scale(1);
    }
  }
  
  /* Hover effects */
  .leaflet-marker-icon:hover {
    transform: scale(1.1);
    transition: transform 0.2s ease;
  }
  
  .selected-marker:hover {
    transform: scale(1.1);
  }
`;


// ========== UTILITY FUNCTIONS ==========
// Funzione helper per verificare se un evento è scaduto
const isEventExpired = (endDate) => {
  if (!endDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventEnd = new Date(endDate);
  eventEnd.setHours(23, 59, 59, 999);
  return eventEnd < today;
};

// Funzione per calcolare la distanza tra due punti (formula di Haversine)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2); 
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
  const distance = R * c;
  return distance;
};

// Funzione per ottenere le immagini di un evento (NON è più un hook)
const getEventImages = (event) => {
  if (!event) return [];

  const images = [];
  
  // Aggiungi immagine principale se presente
  if (event.image?.url) {
    images.push({
      url: event.image.url,
      alt: event.image.alt || event.title || 'Immagine evento',
      isMain: true
    });
  }
  
  // Aggiungi featured_image se presente e diversa da image
  if (event.featured_image?.url && event.featured_image.url !== event.image?.url) {
    images.push({
      url: event.featured_image.url,
      alt: event.featured_image.alt || event.title || 'Immagine evento',
      isMain: false
    });
  }
  
  // Aggiungi immagini dalla gallery se presente
  if (event.gallery && Array.isArray(event.gallery)) {
    event.gallery.forEach(img => {
      if (img.url && !images.some(existingImg => existingImg.url === img.url)) {
        images.push({
          url: img.url,
          alt: img.alt || event.title || 'Immagine evento',
          isMain: false
        });
      }
    });
  }
  
  // Aggiungi immagini dall'array images se presente
  if (event.images && Array.isArray(event.images)) {
    event.images.forEach(img => {
      const imgUrl = img.url || img;
      if (imgUrl && !images.some(existingImg => existingImg.url === imgUrl)) {
        images.push({
          url: imgUrl,
          alt: img.alt || event.title || 'Immagine evento',
          isMain: false
        });
      }
    });
  }
  
  return images;
};

// Servizio di geocodifica per convertire indirizzi in coordinate
const geocodeAddress = async (address) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
    );
    
    if (!response.ok) {
      throw new Error('Errore nella geocodifica dell\'indirizzo');
    }
    
    const data = await response.json();
        
    if (data && data.length > 0) {
      return {
        latitude: parseFloat(data[0].lat),
        longitude: parseFloat(data[0].lon),
        success: true
      };
    } else {
      return { success: false };
    }
  } catch (error) {
    console.error('Errore nella geocodifica:', error);
    return { success: false };
  }
};

// Funzione per creare un debounce
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
          };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// ========== COMPONENTI MAPPA ==========
// Componente per gestire la posizione dell'utente
function LocationMarker({ onLocationFound }) {
  const map = useMapEvents({
    locationfound(e) {
      if (onLocationFound) {
        onLocationFound(e.latlng);
      }
      map.flyTo(e.latlng, 11);
    },
    locationerror(e) {
      console.error("Errore di geolocalizzazione:", e.message);
      alert("Non è stato possibile trovare la tua posizione. Verifica che la geolocalizzazione sia attivata nel tuo browser.");
    }
  });
  
  return null;
}

// Componente per centrare la mappa su una posizione
function SetViewOnClick({ coords, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (coords) {
      map.setView(coords, zoom || map.getZoom());
    }
  }, [coords, zoom, map]);
  return null;
}

const ClusterLayer = ({ events, onMarkerClick, selectedEventId, onOverlappingClick }) => {
  const map = useMap();
  const [processedEvents, setProcessedEvents] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const clusterGroupRef = useRef(null);
  
  useEffect(() => {
    const processEvents = async () => {
      setIsProcessing(true);
      const processed = [...events];
      let updated = false;
      
      for (let i = 0; i < processed.length; i++) {
        const event = processed[i];
        
        if (event.latitude && event.longitude) {
          continue;
        }
        
        if (event.location) {
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
          const result = await geocodeAddress(event.location);
          
          if (result.success) {
            processed[i] = {
              ...event,
              latitude: result.latitude,
              longitude: result.longitude
            };
            updated = true;
          }
        }
      }
      
      setProcessedEvents(processed);
      setIsProcessing(false);
    };
    
    processEvents();
  }, [events]);
  
  useEffect(() => {
    if (isProcessing || processedEvents.length === 0) return;
    
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }
    
    const clusterGroup = L.markerClusterGroup({
      chunkedLoading: true,
      spiderfyOnMaxZoom: false,
      showCoverageOnHover: false,
      maxClusterRadius: 50,
      iconCreateFunction: function(cluster) {
        const childCount = cluster.getChildCount();
        return L.divIcon({
          html: `<div class="cluster-marker">${childCount}</div>`,
          className: 'custom-cluster',
          iconSize: [40, 40]
        });
      }
    });

        clusterGroupRef.current = clusterGroup;

    
    // Raggruppa eventi per posizione molto vicina (raggio di circa 100 metri)
    const positionGroups = new Map();
    const PROXIMITY_THRESHOLD = 0.001; // Circa 100 metri
    
    processedEvents.forEach(event => {
      if (event.latitude && event.longitude) {
        const lat = parseFloat(event.latitude);
        const lng = parseFloat(event.longitude);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          // Cerca se esiste già un gruppo vicino
          let foundGroup = null;
          
          for (let [key, group] of positionGroups.entries()) {
            const [groupLat, groupLng] = key.split(',').map(Number);
            const distance = Math.sqrt(
              Math.pow(lat - groupLat, 2) + Math.pow(lng - groupLng, 2)
            );
            
            if (distance <= PROXIMITY_THRESHOLD) {
              foundGroup = key;
              break;
            }
          }
          
          if (foundGroup) {
            positionGroups.get(foundGroup).push(event);
          } else {
            const positionKey = `${lat},${lng}`;
            positionGroups.set(positionKey, [event]);
          }
        }
      }
    });
    
   // Crea marker per ogni gruppo di posizione
    positionGroups.forEach((eventsInPosition, positionKey) => {
      const [lat, lng] = positionKey.split(',').map(Number);
      
      if (eventsInPosition.length === 1) {
        // Singolo evento - controlla se è selezionato
        const event = eventsInPosition[0];
        const isSelected = selectedEventId === event.id;
        const iconToUse = createCategoryIcon(event.categories, isSelected);
        
        const marker = L.marker([lat, lng], { 
          icon: iconToUse,
          zIndexOffset: isSelected ? 1000 : 0 // Porta in primo piano se selezionato
        });
        
        const macroCategory = getMacroCategory(event.categories);
        const categoryConfig = CATEGORY_STYLES[macroCategory];
        
        marker.bindPopup(`
          <div>
            <h3 style="margin: 0 0 8px 0; color: ${categoryConfig.color};">
<i class="${categoryConfig.icon}" style="margin-right: 5px;"></i>
              ${event.title}
            </h3>
            <p style="margin: 0; font-size: 12px; color: #666;">
              <strong>${categoryConfig.name}</strong>
            </p>
            <p style="margin: 5px 0 0 0; font-size: 11px;">
              ${event.description ? event.description.substring(0, 100) + '...' : ''}
            </p>
          </div>
        `);
        
        marker.on('click', () => {
          onMarkerClick(event);
        });
        
        clusterGroup.addLayer(marker);
      } else {
        // Eventi multipli nella stessa posizione
        // Controlla se uno degli eventi è selezionato
        const hasSelectedEvent = eventsInPosition.some(e => e.id === selectedEventId);
        
        const iconToUse = new Icon({
          iconUrl: '/marker-icon-multiple.png',
          iconSize: hasSelectedEvent ? [55, 45] : [45, 35], // Più grande se contiene evento selezionato
          iconAnchor: hasSelectedEvent ? [27, 45] : [22, 35],
          popupAnchor: [1, -34],
          className: hasSelectedEvent ? 'multiple-events-marker selected' : 'multiple-events-marker'
        });
        
        const marker = L.marker([lat, lng], { 
          icon: iconToUse,
          zIndexOffset: hasSelectedEvent ? 1000 : 0 // Porta in primo piano se contiene evento selezionato
        });
        
        marker.bindPopup(`
          <div style="text-align: center;">
            <h3 style="margin: 0 0 8px 0; color: ${COLORS.primary || '#f39c12'};">
              <i class="fas fa-layer-group" style="margin-right: 5px;"></i>
              ${eventsInPosition.length} Eventi qui
              ${hasSelectedEvent ? ' <i class="fas fa-star" style="color: #ffd700; margin-left: 5px;"></i>' : ''}
            </h3>
            <p style="margin: 0; font-size: 12px; color: #666;">
              Clicca per vedere l'elenco completo
            </p>
          </div>
        `);
        
        marker.on('click', (e) => {
          marker.closePopup();
          
          const containerPoint = map.latLngToContainerPoint([lat, lng]);
          const mapContainer = map.getContainer();
          const rect = mapContainer.getBoundingClientRect();
          
          const x = rect.left + containerPoint.x;
          const y = rect.top + containerPoint.y;
          
          onOverlappingClick(eventsInPosition, { x, y });
        });
        
        clusterGroup.addLayer(marker);
      }
    });

    // Gestisci il click sui cluster
    clusterGroup.on('clusterclick', function(e) {
      const cluster = e.layer;
      const childMarkers = cluster.getAllChildMarkers();
      
      // Se il cluster ha pochi elementi e siamo a zoom alto, mostra la lista
      if (childMarkers.length <= 10 && map.getZoom() >= 15) {
        const eventsInCluster = childMarkers.map(marker => {
          // Trova l'evento corrispondente al marker
          const markerLatLng = marker.getLatLng();
          return processedEvents.find(event => {
            const eventLat = parseFloat(event.latitude);
            const eventLng = parseFloat(event.longitude);
            return Math.abs(eventLat - markerLatLng.lat) < 0.0001 && 
                   Math.abs(eventLng - markerLatLng.lng) < 0.0001;
          });
        }).filter(Boolean);
        
        if (eventsInCluster.length > 0) {
          // Calcola la posizione del popup
          const containerPoint = map.latLngToContainerPoint(cluster.getLatLng());
          const mapContainer = map.getContainer();
          const rect = mapContainer.getBoundingClientRect();
          
          const x = rect.left + containerPoint.x;
          const y = rect.top + containerPoint.y;
          
          // Chiama la funzione per gestire eventi sovrapposti
          onOverlappingClick(eventsInCluster, { x, y });
        }
      } else {
        // Comportamento normale del cluster - zoom in
        cluster.zoomToBounds();
      }
    });
    
    map.addLayer(clusterGroup);
    
    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
        clusterGroupRef.current = null;
      }
    };
  }, [processedEvents, map, onMarkerClick, selectedEventId, onOverlappingClick, isProcessing]);
  
  return null;
};

// ========== COMPONENTE EVENTI VICINI AGGIORNATO ==========
const NearbyEvents = ({ userLocation, events, onEventClick }) => {
  const [nearbyEvents, setNearbyEvents] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const scrollRef = useRef(null); // Ref per il contenitore scroll
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (!userLocation || !events.length) {
      setNearbyEvents([]);
      return;
    }

    // Calcola distanze e filtra eventi entro 50km
    const eventsWithDistance = events
      .map(event => {
        if (!event.latitude || !event.longitude) return null;
        
        const distance = calculateDistance(
          userLocation.lat,
          userLocation.lng,
          parseFloat(event.latitude),
          parseFloat(event.longitude)
        );
        
        return {
          ...event,
          distance: Math.round(distance * 10) / 10 // Arrotonda a 1 decimale
        };
      })
      .filter(event => event && event.distance <= 50) // Solo eventi entro 50km
      .filter(event => {
        // Filtra eventi non scaduti
        const endDate = event.endDate || event.startDate || event.date;
        return !isEventExpired(endDate);
      })
      .sort((a, b) => a.distance - b.distance) // Ordina per distanza
      .slice(0, 50); // Limita a 50 eventi

    setNearbyEvents(eventsWithDistance);
  }, [userLocation, events]);

  // Funzioni per la navigazione con le frecce
  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Controlla se si può scorrere a sinistra o destra
  const checkScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Aggiorna i pulsanti quando cambiano gli eventi o dopo il mount
  useEffect(() => {
    checkScrollButtons();
    
    // Aggiungi listener per aggiornare i pulsanti durante lo scroll
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', checkScrollButtons);
      return () => scrollElement.removeEventListener('scroll', checkScrollButtons);
    }
  }, [nearbyEvents]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  if (!userLocation || nearbyEvents.length === 0) {
    return null;
  }

  return (
    <NearbyEventsContainer isCollapsed={isCollapsed}>
      <NearbyEventsHeaderContainer>
        <NearbyEventsHeader>
          Eventi vicini a te ({nearbyEvents.length})
        </NearbyEventsHeader>
        <CollapseButton 
          onClick={toggleCollapse}
          title={isCollapsed ? "Espandi eventi vicini" : "Riduci eventi vicini"}
        >
          <i className={`fas fa-chevron-${isCollapsed ? 'up' : 'down'}`}></i>
        </CollapseButton>
      </NearbyEventsHeaderContainer>
      
      <NearbyEventsContent isCollapsed={isCollapsed}>
        <ScrollContainer>
          {/* Pulsante freccia sinistra */}
          <ScrollButton 
            className="scroll-button"
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            title="Scorri a sinistra"
          >
            <i className="fas fa-chevron-left"></i>
          </ScrollButton>
          
          <NearbyEventsScrollWrapper>
            <NearbyEventsScroll ref={scrollRef}>
              {nearbyEvents.map(event => {
                // Usa la funzione normale (non più hook) per gestire le immagini
                const eventImages = getEventImages(event);
                const primaryImage = eventImages.length > 0 ? eventImages[0] : null;
                
                // Ottieni la macro categoria per il colore del bordo
                const macroCategory = getMacroCategory(event.categories);
                const categoryConfig = CATEGORY_STYLES[macroCategory];
                
                return (
                  <NearbyEventCard 
                    key={event.id} 
                    onClick={() => onEventClick(event)}
                    style={{
                      borderLeft: `4px solid ${categoryConfig.color}`
                    }}
                  >
                    <CardImage 
                      src={primaryImage?.url}
                      style={{
                        backgroundImage: primaryImage?.url ? `url(${primaryImage.url})` : 'none'
                      }}
                    >
                      {!primaryImage?.url && (
                        <i className={categoryConfig.icon} style={{ color: categoryConfig.color }}></i>
                      )}
                      <CardOverlay>
                        {event.title?.length > 50 
                          ? `${event.title.substring(0, 50)}...` 
                          : event.title || 'Evento senza titolo'
                        }
                      </CardOverlay>
                    </CardImage>
                    <CardInfo>
                      <DistanceText>
                        {event.distance < 1 
                          ? `${Math.round(event.distance * 1000)} m`
                          : `${event.distance} km`
                        }
                      </DistanceText>
                    </CardInfo>
                  </NearbyEventCard>
                );
              })}
            </NearbyEventsScroll>
          </NearbyEventsScrollWrapper>
          
          {/* Pulsante freccia destra */}
          <ScrollButton 
            className="scroll-button"
            onClick={scrollRight}
            disabled={!canScrollRight}
            title="Scorri a destra"
          >
            <i className="fas fa-chevron-right"></i>
          </ScrollButton>
        </ScrollContainer>
      </NearbyEventsContent>
    </NearbyEventsContainer>
  );
};

const formatMonthYear = (date) => {
  return format(date, 'MMMM yyyy', { locale: it });
};

const formatEventDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return format(date, 'dd/MM', { locale: it });
};

const formatEventTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return format(date, 'HH:mm', { locale: it });
};

const groupEventsByMonth = (events) => {
  // Ordina gli eventi per data
  const sortedEvents = events
    .filter(event => event.startDate || event.date)
    .sort((a, b) => {
      const dateA = new Date(a.startDate || a.date);
      const dateB = new Date(b.startDate || b.date);
      return dateA - dateB;
    });

  // Raggruppa per mese
  const groupedEvents = {};
  
  sortedEvents.forEach(event => {
    const eventDate = new Date(event.startDate || event.date);
    const monthKey = format(eventDate, 'yyyy-MM', { locale: it });
    const monthLabel = formatMonthYear(eventDate);
    
    if (!groupedEvents[monthKey]) {
      groupedEvents[monthKey] = {
        label: monthLabel,
        events: []
      };
    }
    
    groupedEvents[monthKey].events.push(event);
  });

  // Converte in array e ordina per mese
  return Object.entries(groupedEvents)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, value]) => value);
};



// ========== COMPONENTE PRINCIPALE ==========
const EventMap = ({ events: propEvents = [], onSearch }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [mapCenter, setMapCenter] = useState([44.1155, 8.9442]);
  const [mapZoom, setMapZoom] = useState(9);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [searchRadius, setSearchRadius] = useState(50);
  const [showRadiusCircle, setShowRadiusCircle] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [eventLoading, setEventLoading] = useState(false);
  const [eventError, setEventError] = useState(null);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [strapiEvents, setStrapiEvents] = useState([]);
  const [strapiCategories, setStrapiCategories] = useState([]);
  const [loadingStrapi, setLoadingStrapi] = useState(false);
  const [geocodingInProgress, setGeocodingInProgress] = useState(false);
  const [geocodingProgress, setGeocodingProgress] = useState(0);
  const [showNearbyEvents, setShowNearbyEvents] = useState(false);
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = width <= 768;
  const [overlappingEvents, setOverlappingEvents] = useState([]);
  const [showOverlappingPopup, setShowOverlappingPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [showEventCounter, setShowEventCounter] = useState(true);
  
  // Stati per il suggerimento eventi
  const [showSuggestPopup, setShowSuggestPopup] = useState(false);
  const [suggestForm, setSuggestForm] = useState({
    title: '',
    description: '',
    location: '',
    cost: '',
    category: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: ''
  });
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState('');
  const [activeFilters, setActiveFilters] = useState({});

  
  // Funzione per cercare suggerimenti
  const fetchSuggestions = async (query) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`
      );
      
      if (!response.ok) {
        throw new Error('Errore nella ricerca suggerimenti');
      }
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        setSuggestions(data.map(item => ({
          displayName: item.display_name,
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon)
        })));
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Errore nella ricerca suggerimenti:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Debounce per la ricerca di suggerimenti
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query && query.length >= 3) {
        fetchSuggestions(query);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 500),
    []
  );


  useEffect(() => {
    const loadStrapiData = async () => {
      setLoadingStrapi(true);
      try {
        const [eventsData, categoriesData] = await Promise.all([
          fetchEvents(),
          fetchCategories()
        ]);
        
        setStrapiEvents(eventsData);
        setStrapiCategories(categoriesData);
      } catch (error) {
        console.error('Errore nel caricamento dati da Strapi:', error);
      } finally {
        setLoadingStrapi(false);
      }
    };
    
    loadStrapiData();
  }, []);
  
  // Chiudi la barra di ricerca quando si passa alla visualizzazione desktop
  useEffect(() => {
    if (!isMobile && isSearchOpen) {
      setIsSearchOpen(false);
    }
  }, [isMobile, isSearchOpen]);

  useEffect(() => {
    // Inizializza EmailJS
    if (process.env.REACT_APP_EMAILJS_PUBLIC_KEY) {
      emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
    } else {
      console.error('EmailJS Public Key non configurata');
    }

    const loadMapData = async () => {
      try {
        setLoading(true);
        // Carica solo i dati essenziali per la mappa
        const [mapEventsData, categoriesData] = await Promise.all([
          fetchEventsForMap(), // Dati minimali
          fetchCategories()
        ]);
        
        setEvents(mapEventsData);
        setCategories(categoriesData);
        setError(null);
      } catch (err) {
        console.error('Errore nel caricamento dei dati mappa:', err);
        setError('Si è verificato un errore nel caricamento dei dati. Riprova più tardi.');
      } finally {
        setLoading(false);
      }
    };
    
    loadMapData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSuggestions && !event.target.closest('.search-container')) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  // Funzione per cercare un luogo e centrare la mappa
  const handleSearchSubmit = async (e) => {
  e.preventDefault();
  if (searchQuery.trim()) {
    setIsSearching(true);
    setSearchError('');
    setShowSuggestions(false);
    
    // Traccia la ricerca
    trackSearch(searchQuery);
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`
      );
      
      if (!response.ok) {
        throw new Error('Errore nella ricerca del luogo');
      }
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        const location = {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          displayName: data[0].display_name
        };
        
        setMapCenter([location.lat, location.lng]);
        setMapZoom(13);
        setUserLocation({
          lat: location.lat,
          lng: location.lng
        });
        setShowNearbyEvents(true);
      } else {
        setSearchError('Nessun risultato trovato per la ricerca');
      }
      } catch (error) {
      console.error('Errore nella ricerca:', error);
      setSearchError('Si è verificato un errore durante la ricerca');
    } finally {
      setIsSearching(false);
      
      if (isMobile) {
        setIsSearchOpen(false);
      }
    }
  }
};

  const toggleMobileSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  // Gestisci il click su un suggerimento
  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.displayName.split(',')[0]);
    setShowSuggestions(false);
    
    setMapCenter([suggestion.lat, suggestion.lng]);
    setMapZoom(13);
    setUserLocation({
      lat: suggestion.lat,
      lng: suggestion.lng
    });
    setShowNearbyEvents(true);
  };

const removeFilter = (filterType, value = null) => {
    const { resetUserLocation, ...currentFilters } = activeFilters;
    const updatedFilters = { ...currentFilters };

    switch (filterType) {
        case 'searchText':
            delete updatedFilters.searchText;
            break;
        case 'locationText':
            delete updatedFilters.locationText;
            break;
        case 'category':
            if (updatedFilters.categories) {
                updatedFilters.categories = updatedFilters.categories.filter(id => id !== value);
                if (updatedFilters.categories.length === 0) delete updatedFilters.categories;
            }
            break;
        case 'dateRange':
            delete updatedFilters.startDate;
            delete updatedFilters.endDate;
            break;
        case 'radius':
            delete updatedFilters.searchRadius;
            break;
        default:
            break;
    }
    handleApplyFilters(updatedFilters);
};

const clearAllFilters = () => {
  resetFilters();
  // Opzionale: riapri il panel filtri per permettere nuova selezione
  // setIsFilterOpen(true);
};

// ✅ NUOVO: Funzione per generare i tag dei filtri attivi
const renderActiveFilterTags = () => {
    if (!hasAppliedFilters) return null;
    
    const tags = [];

    if (activeFilters.searchText) {
        tags.push(
            <FilterTag key="search"><FilterTagIcon className="fas fa-search" /><FilterTagText>"{activeFilters.searchText}"</FilterTagText><FilterTagRemove onClick={() => removeFilter('searchText')}>×</FilterTagRemove></FilterTag>
        );
    }
    if (activeFilters.locationText) {
        tags.push(
            <FilterTag key="location"><FilterTagIcon className="fas fa-map-pin" /><FilterTagText>Luogo: "{activeFilters.locationText}"</FilterTagText><FilterTagRemove onClick={() => removeFilter('locationText')}>×</FilterTagRemove></FilterTag>
        );
    }
    if (activeFilters.categories?.length > 0) {
        activeFilters.categories.forEach(catId => {
            const category = categories.find(c => c.id === catId);
            if (category) {
                const macro = getMacroCategoryForCategory(catId);
                tags.push(
                    <FilterTag key={`cat-${catId}`}><FilterTagIcon className={macro.icon} color={macro.color} /><FilterTagText>{category.name}</FilterTagText><FilterTagRemove onClick={() => removeFilter('category', catId)}>×</FilterTagRemove></FilterTag>
                );
            }
        });
    }
    if (activeFilters.startDate || activeFilters.endDate) {
        let dateText = '';
        if (activeFilters.startDate) dateText += `Da ${format(new Date(activeFilters.startDate), 'dd/MM')}`;
        if (activeFilters.endDate) dateText += ` a ${format(new Date(activeFilters.endDate), 'dd/MM')}`;
        tags.push(
            <FilterTag key="date"><FilterTagIcon className="fas fa-calendar-alt" /><FilterTagText>{dateText}</FilterTagText><FilterTagRemove onClick={() => removeFilter('dateRange')}>×</FilterTagRemove></FilterTag>
        );
    }
    if (activeFilters.searchRadius) {
        tags.push(
            <FilterTag key="radius"><FilterTagIcon className="fas fa-map-marker-alt" /><FilterTagText>Raggio {activeFilters.searchRadius}km</FilterTagText><FilterTagRemove onClick={() => removeFilter('radius')}>×</FilterTagRemove></FilterTag>
        );
    }

    if (tags.length === 0) return null;

    return (
        <ActiveFiltersContainer>
            {tags}
            {tags.length > 1 && (
                <ClearAllFiltersButton onClick={resetFilters}>
                    <i className="fas fa-times" style={{ marginRight: '6px' }}></i>
                    Rimuovi tutti
                </ClearAllFiltersButton>
            )}
        </ActiveFiltersContainer>
    );
};

const handleApplyFilters = (filters) => {
  // Reset posizione utente se richiesto
  if (filters.resetUserLocation) {
    setUserLocation(null);
    setShowNearbyEvents(false);
    setShowRadiusCircle(false);
    setMapCenter([44.1155, 8.9442]);
    setMapZoom(9);
    setSearchQuery('');
  }

  let filtered = [...events];
  trackFilterUsage('Apply Filters', JSON.stringify(filters));

  // Filtro per ricerca testuale
  if (filters.searchText && filters.searchText.length >= 2) {
    const normalizedSearchTerm = filters.searchText.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    filtered = filtered.filter(event => {
      const searchFields = [
        event.title || '', event.description || '', event.location || '',
        ...(event.categories ? event.categories.map(cat => cat.name || '') : [])
      ].join(' ').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return searchFields.includes(normalizedSearchTerm);
    });
  }
  
  // ✅ NUOVO: Filtro per ricerca località
  if (filters.locationText && filters.locationText.length > 0) {
    const normalizedLocation = filters.locationText.toLowerCase().trim();
    filtered = filtered.filter(event => 
      event.location && event.location.toLowerCase().includes(normalizedLocation)
    );
  }

  // Filtro per categorie
  if (filters.categories && filters.categories.length > 0) {
    filtered = filtered.filter(event =>
      event.categories && event.categories.some(cat => filters.categories.includes(cat.id))
    );
  }

  // Filtro per date
  if (filters.startDate || filters.endDate) {
      filtered = filtered.filter(event => {
          const eventStartDate = new Date(event.startDate || event.date);
          const eventEndDate = new Date(event.endDate || event.startDate || event.date);
          const filterStartDate = filters.startDate ? new Date(filters.startDate) : null;
          const filterEndDate = filters.endDate ? new Date(filters.endDate) : null;
          if(filterEndDate) filterEndDate.setHours(23, 59, 59, 999);

          if (filterStartDate && filterEndDate) return eventStartDate <= filterEndDate && eventEndDate >= filterStartDate;
          if (filterStartDate) return eventEndDate >= filterStartDate;
          if (filterEndDate) return eventStartDate <= filterEndDate;
          return false;
      });
  }

  // Filtro per raggio
  if (userLocation && filters.searchRadius) {
    setSearchRadius(filters.searchRadius);
    setShowRadiusCircle(true);
    filtered = filtered.filter(event => {
      if (!event.latitude || !event.longitude) return false;
      const distance = calculateDistance(userLocation.lat, userLocation.lng, parseFloat(event.latitude), parseFloat(event.longitude));
      return distance <= filters.searchRadius;
    });
  } else {
    setShowRadiusCircle(false);
  }

  setFilteredEvents(filtered);
  setActiveFilters(filters);
  setHasAppliedFilters(Object.keys(filters).some(key => filters[key] && (!Array.isArray(filters[key]) || filters[key].length > 0) && key !== 'resetUserLocation'));
  setIsFilterOpen(false);
};


    const resetFilters = () => {
        setFilteredEvents([]);
        setActiveFilters({});
        setHasAppliedFilters(false);
        setShowRadiusCircle(false);
        // Applica filtri vuoti per resettare tutto
        handleApplyFilters({}); 
    };



    const handleMarkerClick = async (eventItem) => {
      setEventLoading(true);
      setEventError(null);
      
      // Se clicchi sullo stesso evento, deseleziona
      if (selectedEvent && selectedEvent.id === eventItem.id) {
        setSelectedEvent(null);
        setIsSidebarOpen(false);
        setEventLoading(false);
        return;
      }
      
      setSelectedEvent(eventItem);
      setIsSidebarOpen(true);
      
      // Traccia la visualizzazione dell'evento
      trackEventView(eventItem.id, eventItem.title);
      
      try {
        const fullEventData = await fetchEventDetails(eventItem.id);
        setSelectedEvent(fullEventData);
      } catch (error) {
        console.error('Errore nel caricamento dettagli evento:', error);
        setEventError('Errore nel caricamento dei dettagli dell\'evento');
      } finally {
        setEventLoading(false);
      }
    };

    

    const handleCloseSidebar = () => {
      setIsSidebarOpen(false);
      setTimeout(() => {
        setSelectedEvent(null);
      }, 300);
    };
    
    const handleLocationFound = (location) => {
      setUserLocation(location);
      setMapCenter([location.lat, location.lng]);
      setMapZoom(11);
      setShowNearbyEvents(true); // Mostra gli eventi vicini quando si trova la posizione
    };

    const requestUserLocation = () => {
      // Traccia la richiesta di geolocalizzazione
      trackLocationRequest();
      
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            handleLocationFound(location);
          },
          (error) => {
            console.error("Errore di geolocalizzazione:", error.message);
            alert("Non è stato possibile trovare la tua posizione. Verifica che la geolocalizzazione sia attivata nel tuo browser.");
          },
          { enableHighAccuracy: true }
        );
      } else {
        console.log("La geolocalizzazione non è supportata dal tuo browser.");
      }
    };

  // Determina quali eventi mostrare - ESCLUDI EVENTI SCADUTI
  const allEvents = hasAppliedFilters ? filteredEvents : events;
  const displayEvents = allEvents.filter(event => {
    const endDate = event.endDate || event.startDate || event.date;
    return !isEventExpired(endDate);
  });
  const noResults = hasAppliedFilters && filteredEvents.length === 0;

  // Componente per mostrare il progresso della geocodifica
  const renderGeocodingProgress = () => {
    if (!geocodingInProgress) return null;
    
    return (
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1500,
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
        textAlign: 'center',
        minWidth: '300px'
      }}>
        <div>Conversione degli indirizzi in coordinate...</div>
        <div style={{ marginTop: '10px' }}>{Math.round(geocodingProgress)}%</div>
        <div style={{ 
                    marginTop: '8px', 
          height: '6px', 
          background: '#f0f0f0',
          borderRadius: '3px'
        }}>
          <div style={{ 
            height: '100%', 
            width: `${geocodingProgress}%`,
            background: COLORS.primary || '#f39c12',
            borderRadius: '3px',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>
    );
  };

    // Aggiungi questa funzione nel tuo componente EventMap
  const handleOverlappingEvents = (events, position) => {
    // Traccia l'apertura del popup eventi sovrapposti
    trackOverlappingEventsPopup(events.length);
    
    setOverlappingEvents(events);
    setPopupPosition(position);
    setShowOverlappingPopup(true);
  };

  const closeOverlappingPopup = () => {
    setShowOverlappingPopup(false);
    setOverlappingEvents([]);
  };

  // Aggiungi questa funzione nel componente EventMap, prima del return

const getEventStats = () => {
  const totalEvents = events.length;
  const visibleEvents = displayEvents.length;
  const hasFilters = hasAppliedFilters;
  
  // ✅ NUOVO: Determina i tipi di filtri attivi
  const activeFilters = [];
  if (filteredEvents.length > 0) {
    // Logica per determinare quali filtri sono attivi basandosi sulla differenza tra events e filteredEvents
    if (totalEvents !== filteredEvents.length) {
      activeFilters.push('filtri attivi');
    }
  }
  
  return {
    total: totalEvents,
    visible: visibleEvents,
    filtered: hasFilters,
    percentage: totalEvents > 0 ? Math.round((visibleEvents / totalEvents) * 100) : 0,
    activeFilters
  };
};


  useEffect(() => {
  // Mostra sempre il contatore quando cambiano gli eventi o i filtri
  setShowEventCounter(true);
  
  const timer = setTimeout(() => {
    setShowEventCounter(false);
  }, 10000);

  return () => clearTimeout(timer);
}, [displayEvents.length, hasAppliedFilters]);

  const handleEventFromPopup = (event) => {
    closeOverlappingPopup();
    handleMarkerClick(event);
  };

  // Funzioni per gestire il suggerimento eventi
const openSuggestPopup = () => {
  setShowSuggestPopup(true);
  setSendSuccess(false);
  setSendError('');
};

const closeSuggestPopup = () => {
  setShowSuggestPopup(false);
  setSuggestForm({
    title: '',
    description: '',
    location: '',
    cost: '',
    category: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: ''
  });
  setSendSuccess(false);
  setSendError('');
};

const handleFormChange = (field, value) => {
  setSuggestForm(prev => ({
    ...prev,
    [field]: value
  }));
};

const sendSuggestionEmail = async (formData) => {
  try {
    // Controlla che tutte le variabili siano configurate
    if (!process.env.REACT_APP_EMAILJS_SERVICE_ID || !process.env.REACT_APP_EMAILJS_TEMPLATE_ID) {
      throw new Error('Configurazione EmailJS incompleta');
    }

    const templateParams = {
      event_title: formData.title,
      event_description: formData.description,
      event_location: formData.location,
      event_cost: formData.cost || 'Non specificato',
      event_category: categoryOptions.find(cat => cat.value === formData.category)?.label || formData.category,
      start_date: formData.startDate,
      start_time: formData.startTime || 'Non specificato',
      end_date: formData.endDate || 'Non specificato',
      end_time: formData.endTime || 'Non specificato',
      timestamp: new Date().toLocaleString('it-IT')
    };

    console.log('Invio email con parametri:', templateParams); // Per debug

    const result = await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      templateParams
    );

    console.log('Email inviata con successo:', result);
    return { success: true };
    
  } catch (error) {
    console.error('Errore invio email:', error);
    return { 
      success: false, 
      error: error.text || error.message || 'Errore nell\'invio dell\'email'
    };
  }
};

const handleSubmitSuggestion = async (e) => {
  e.preventDefault();
  
  // Validazione form
  if (!suggestForm.title.trim()) {
    setSendError('Il titolo è obbligatorio');
    return;
  }
  
  if (!suggestForm.description.trim()) {
    setSendError('La descrizione è obbligatoria');
    return;
  }
  
  if (!suggestForm.location.trim()) {
    setSendError('Il luogo è obbligatorio');
    return;
  }
  
  if (!suggestForm.category) {
    setSendError('Seleziona una tipologia di evento');
    return;
  }
  
  if (!suggestForm.startDate) {
    setSendError('La data di inizio è obbligatoria');
    return;
  }
  
  setIsSending(true);
  setSendError('');
  
  try {
    const result = await sendSuggestionEmail(suggestForm);
    
    if (result.success) {
      setSendSuccess(true);
      // Chiudi automaticamente dopo 3 secondi
      setTimeout(() => {
        closeSuggestPopup();
      }, 3000);
    } else {
      setSendError(result.error || 'Errore nell\'invio del suggerimento');
    }
  } catch (error) {
    setSendError('Errore nell\'invio del suggerimento');
  } finally {
    setIsSending(false);
  }
};

// Opzioni per le categorie (basate sui tuoi CATEGORY_STYLES)
const categoryOptions = [
  { value: 'spettacolo', label: 'Spettacolo (Teatro, Cinema, Concerti...)' },
  { value: 'culinaria', label: 'Enogastronomia (Sagre, Gastronomia...)' },
  { value: 'sport', label: 'Sport & Movimento (Corsa, Trekking...)' },
  { value: 'cultura', label: 'Arte & Cultura (Mostre, Tradizioni...)' },
  { value: 'eventi', label: 'Festival & Eventi' },
  { value: 'natura', label: 'Natura & Outdoor' },
  { value: 'lifestyle', label: 'Lifestyle & Formazione' },
  { value: 'Tecnologia', label: 'Digitale & Tech' },
  { value: 'default', label: 'Altro' }
];


  return (
    <MapWrapper>
      {/* Mostra messaggio di caricamento */}
      {loading && !geocodingInProgress && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          Caricamento eventi in corso...
        </div>
      )}
      
      {/* Mostra progresso geocodifica */}
      {renderGeocodingProgress()}
      
      {/* Mostra messaggio di errore */}
      {error && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          color: 'red'
        }}>
          {error}
        </div>
      )}
      
      <TopControlsContainer>
        <LogoOverlay>
          <img 
            src="/logo.png" 
            alt="Finday Logo" 
            className="desktop-logo"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
          />
          <img 
            src="/logo-small.png" 
            alt="Finday Logo" 
            className="mobile-logo"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
          />
        </LogoOverlay>

            {/* ✅ AGGIUNGI LA CHIAMATA ALLA FUNZIONE QUI */}
            {renderActiveFilterTags()}
        
        <ControlsGroup>
          {/* Pulsante ricerca mobile */}
          {isMobile && (
            <MobileSearchButton onClick={toggleMobileSearch}>
              <i className="fas fa-search"></i>
            </MobileSearchButton>
          )}
          
          {/* Barra di ricerca desktop */}
          <SearchBar isSearchOpen={isSearchOpen} className="search-container">
            <form onSubmit={handleSearchSubmit}>
              <input
                type="text"
                placeholder="Cerca un luogo..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  debouncedSearch(e.target.value);
                }}
                onFocus={() => {
                  if (searchQuery.length >= 3) {
                    setShowSuggestions(true);
                  }
                }}
              />
              {!isMobile && (
                <button type="submit" disabled={isSearching}>
                  {isSearching ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-search"></i>}
                </button>
              )}
            </form>
            
            {/* Suggerimenti di ricerca */}
            {showSuggestions && suggestions.length > 0 && (
              <SearchSuggestions>
                {suggestions.map((suggestion, index) => (
                  <div 
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion.displayName}
                  </div>
                ))}
              </SearchSuggestions>
            )}
          </SearchBar>
          
          {/* Pulsante filtri */}
          <UniformButton
            onClick={() => {
              trackMapInteraction('Filter Button Click');
              setIsFilterOpen(true);
            }}
            title="Apri filtri"
          >
            <i className="fas fa-filter"></i>
          </UniformButton>
          
          {/* Pulsante geolocalizzazione */}
          <UniformButton
            onClick={() => {
              trackMapInteraction('Location Button Click');
              if (userLocation) {
                handleLocationFound(userLocation);
              } else {
                requestUserLocation();
              }
            }}
            title="Trova la mia posizione"
          >
            <i className="fas fa-location-arrow"></i>
          </UniformButton>
        </ControlsGroup>
      </TopControlsContainer>


      <InfoBox isMobile={isMobile} />

      {/* INSERISCI QUI IL CONTATORE EVENTI */}
{showEventCounter && (
  <EventCounter show={showEventCounter} showNearbyEvents={showNearbyEvents}>
    <i className="fas fa-map-marker-alt" style={{ color: COLORS.primary || '#f39c12' }}></i>
    <span className="count-number">{getEventStats().visible}</span>
    <span>
      {getEventStats().visible === 1 ? 'evento' : 'eventi'} sulla mappa
    </span>
    
    {getEventStats().filtered && (
      <span className="filter-indicator">
        <i className="fas fa-filter" style={{ marginRight: '4px' }}></i>
        Filtrati
      </span>
    )}
    
    {getEventStats().filtered && getEventStats().total > getEventStats().visible && (
      <span style={{ fontSize: '12px', color: '#666' }}>
        su {getEventStats().total} totali ({getEventStats().percentage}%)
      </span>
    )}
    
    {/* ✅ NUOVO: Pulsante per aprire i filtri direttamente dal contatore */}
    {getEventStats().filtered && (
      <button 
        onClick={() => setIsFilterOpen(true)}
        style={{
          background: 'rgba(255,255,255,0.9)',
          border: '1px solid #ddd',
          borderRadius: '12px',
          padding: '4px 8px',
          fontSize: '10px',
          cursor: 'pointer',
          marginLeft: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
        title="Modifica filtri"
      >
        <i className="fas fa-edit"></i>
        Modifica
      </button>
    )}
  </EventCounter>
)}


      
      <FilterPanel 
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilters={handleApplyFilters}
        userLocation={userLocation}
        categories={strapiCategories}
      />
      
      
      {noResults && (
        <NoResultsMessage>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <i className="fas fa-search" style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }}></i>
            <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>
              Nessun evento trovato
            </h3>
            <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
              Prova a modificare i filtri o ampliare la ricerca
            </p>
          </div>
          <button onClick={resetFilters} style={{
            background: COLORS.primary || '#f39c12',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            <i className="fas fa-undo" style={{ marginRight: '8px' }}></i>
            Reimposta filtri
          </button>
        </NoResultsMessage>
      )}

      
      {searchError && (
        <NoResultsMessage>
          {searchError}
          <button onClick={() => setSearchError('')}>Chiudi</button>
        </NoResultsMessage>
      )}
      
      {/* Pulsante per la geolocalizzazione */}
            
      <MapContainer 
        center={mapCenter} 
        zoom={mapZoom} 
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
        maxZoom={18}
      >
        <ClusterStyles />
        
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {/* Componente per gestire la richiesta di posizione */}
        <LocationMarker onLocationFound={handleLocationFound} />
        
        {/* Componente per centrare la mappa su una posizione */}
        <SetViewOnClick coords={mapCenter ? mapCenter : null} zoom={mapZoom} />
        
        {/* Mostra il raggio di ricerca se attivo */}
        {userLocation && showRadiusCircle && (
          <Circle 
            center={[userLocation.lat, userLocation.lng]}
            radius={searchRadius * 1000}
            pathOptions={{ 
              fillColor: 'rgba(0, 128, 255, 0.32)',
              color: 'rgba(0, 128, 255, 0.5)',
              weight: 1
            }}
          />
        )}
        
        {/* Marker per la posizione dell'utente */}
        {userLocation && (
          <Marker
            position={[userLocation.lat, userLocation.lng]}
            icon={userLocationIcon}
          >
            <Popup>La tua posizione attuale</Popup>
          </Marker>
        )}
        
        {/* Componente personalizzato per il clustering degli eventi */}
        <ClusterLayer 
          events={displayEvents}
          onMarkerClick={handleMarkerClick}
          selectedEventId={selectedEvent?.id}
          onOverlappingClick={handleOverlappingEvents}
        />
      </MapContainer>
      
      <EventSidebar 
        isOpen={isSidebarOpen}
        event={selectedEvent}
        loading={eventLoading}
        error={eventError}
        onClose={handleCloseSidebar}
      />
      
      {/* Componente Eventi Vicini come overlay */}
      {showNearbyEvents && (
        <NearbyEvents 
          userLocation={userLocation}
          events={displayEvents}
          onEventClick={handleMarkerClick}
          hasActiveFilters={hasAppliedFilters} // Per mostrare se ci sono filtri attivi
          filterCount={filteredEvents.length}
        />
      )}

      {/* Popup per eventi sovrapposti */}
      {showOverlappingPopup && (
        <OverlappingEventsPopup position={popupPosition}>
          <PopupHeader>
            <span>
              <i className="fas fa-map-marker-alt" style={{ marginRight: '8px' }}></i>
              Eventi in questa zona ({overlappingEvents.length})
            </span>
            <PopupCloseButton onClick={closeOverlappingPopup}>
              <i className="fas fa-times"></i>
            </PopupCloseButton>
          </PopupHeader>
          
          {groupEventsByMonth(overlappingEvents).map((monthGroup, monthIndex) => (
            <MonthSection key={monthIndex}>
              <MonthHeader>
                <i className="fas fa-calendar" style={{ marginRight: '6px' }}></i>
                {monthGroup.label}
              </MonthHeader>
              
              {monthGroup.events.map(event => {
                const macroCategory = getMacroCategory(event.categories);
                const categoryConfig = CATEGORY_STYLES[macroCategory];
                const eventDate = event.startDate || event.date;
                
                return (
                  <EventListItem 
                    key={event.id} 
                    onClick={() => handleEventFromPopup(event)}
                  >
                    <EventItemTitle>{event.title}</EventItemTitle>
                    
                    <EventItemDetails>
                      <EventItemCategory color={categoryConfig.color}>
                        <i className={categoryConfig.icon} style={{ marginRight: '4px' }}></i>
                        {categoryConfig.name}
                      </EventItemCategory>
                      
                      {eventDate && (
                        <>
                          <EventItemDate>
                            {formatEventDate(eventDate)}
                          </EventItemDate>
                          
                          {formatEventTime(eventDate) !== '00:00' && (
                            <EventItemTime>
                              {formatEventTime(eventDate)}
                            </EventItemTime>
                          )}
                        </>
                      )}
                    </EventItemDetails>
                    
                    {event.location && (
                      <div style={{ 
                        fontSize: '10px', 
                        color: '#868e96', 
                        marginTop: '4px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <i className="fas fa-map-marker-alt" style={{ marginRight: '4px', fontSize: '8px' }}></i>
                        {event.location.length > 30 
                          ? `${event.location.substring(0, 30)}...` 
                          : event.location
                        }
                      </div>
                    )}
                  </EventListItem>
                );
              })}
            </MonthSection>
          ))}
        </OverlappingEventsPopup>
      )}



     {/* PULSANTE DONAZIONE con posizionamento dinamico */}
      <DonationButton
        href="https://www.paypal.com/donate/?business=info@finday.it&no_recurring=0&item_name=Supporta+Finday&currency_code=EUR"
        target="_blank"
        rel="noopener noreferrer"
        title="Supporta il progetto - €2 suggeriti, ma puoi cambiare"
        onClick={() => trackDonationClick()}
      >
        <span className="coffee-icon">☕</span>
        <span className="desktop-text">Offrimi un caffè!</span>
      </DonationButton>
      {/* PULSANTE SUGGERISCI EVENTO */}
      <SuggestEventButton
        as="button"
        onClick={openSuggestPopup}
        title="Suggerisci un nuovo evento"
      >
  <span className="suggest-icon">📅</span>
  <span className="desktop-text">Suggerisci evento</span>
</SuggestEventButton>

{/* POPUP SUGGERIMENTO EVENTO */}
{showSuggestPopup && (
  <SuggestEventPopup onClick={(e) => e.target === e.currentTarget && closeSuggestPopup()}>
    <SuggestEventModal>
      <SuggestEventHeader>
        <h3>
          <i className="fas fa-plus-circle" style={{ marginRight: '8px' }}></i>
          Suggerisci un Evento
        </h3>
        <SuggestEventCloseButton onClick={closeSuggestPopup}>
          <i className="fas fa-times"></i>
        </SuggestEventCloseButton>
      </SuggestEventHeader>
      
      {sendSuccess ? (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', color: '#28a745', marginBottom: '16px' }}>
            ✅
          </div>
          <h4 style={{ color: '#28a745', marginBottom: '8px' }}>
            Suggerimento Inviato!
          </h4>
          <p style={{ color: '#666', margin: '0' }}>
            Grazie per il tuo contributo. Esamineremo il tuo suggerimento al più presto!
          </p>
        </div>
      ) : (
        <SuggestEventForm onSubmit={handleSubmitSuggestion}>
          <FormGroup>
            <label htmlFor="title">
              Titolo Evento *
            </label>
            <input
              type="text"
              id="title"
              placeholder="es. Concerto di musica classica"
              value={suggestForm.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              maxLength={100}
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="description">
              Descrizione *
            </label>
            <textarea
              id="description"
              placeholder="Descrivi l'evento, cosa succederà, chi partecipa..."
              value={suggestForm.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              maxLength={500}
            />
            <div style={{ fontSize: '12px', color: '#999', textAlign: 'right' }}>
              {suggestForm.description.length}/500 caratteri
            </div>
          </FormGroup>

          <FormGroup>
            <label htmlFor="location">
              Luogo *
            </label>
            <input
              type="text"
              id="location"
              placeholder="es. Teatro Carlo Felice, Genova"
              value={suggestForm.location}
              onChange={(e) => handleFormChange('location', e.target.value)}
              maxLength={100}
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="category">
              Tipologia Evento *
            </label>
            <select
              id="category"
              value={suggestForm.category}
              onChange={(e) => handleFormChange('category', e.target.value)}
            >
              <option value="">Seleziona una tipologia...</option>
              {categoryOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FormGroup>

          <FormGroup>
            <label htmlFor="cost">
              Costo (opzionale)
            </label>
            <input
              type="text"
              id="cost"
              placeholder="es. Gratuito, 15€, 10-25€"
              value={suggestForm.cost}
              onChange={(e) => handleFormChange('cost', e.target.value)}
              maxLength={50}
            />
          </FormGroup>

                    <DateTimeGroup>
            <FormGroup>
              <label htmlFor="startDate">
                Data Inizio *
              </label>
              <input
                type="date"
                id="startDate"
                value={suggestForm.startDate}
                onChange={(e) => handleFormChange('startDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="startTime">
                Ora Inizio
              </label>
              <input
                type="time"
                id="startTime"
                value={suggestForm.startTime}
                onChange={(e) => handleFormChange('startTime', e.target.value)}
              />
            </FormGroup>
          </DateTimeGroup>

          <DateTimeGroup>
            <FormGroup>
              <label htmlFor="endDate">
                Data Fine
              </label>
              <input
                type="date"
                id="endDate"
                value={suggestForm.endDate}
                onChange={(e) => handleFormChange('endDate', e.target.value)}
                min={suggestForm.startDate || new Date().toISOString().split('T')[0]}
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="endTime">
                Ora Fine
              </label>
              <input
                type="time"
                id="endTime"
                value={suggestForm.endTime}
                onChange={(e) => handleFormChange('endTime', e.target.value)}
              />
            </FormGroup>
          </DateTimeGroup>

          {sendError && (
            <div style={{
              background: '#f8d7da',
              color: '#721c24',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              border: '1px solid #f5c6cb',
              fontSize: '14px'
            }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
              {sendError}
            </div>
          )}

          <FormActions>
            <CancelButton type="button" onClick={closeSuggestPopup}>
              Annulla
            </CancelButton>
            <SubmitButton type="submit" disabled={isSending}>
              {isSending && <LoadingSpinner />}
              {isSending ? 'Invio in corso...' : 'Invia Suggerimento'}
            </SubmitButton>
          </FormActions>

          <FormNote>
            <i className="fas fa-info-circle" style={{ marginRight: '8px', color: '#28a745' }}></i>
            <strong>Nota:</strong> Il tuo suggerimento verrà inviato al team di Finday per la revisione. 
            Grazie per contribuire a rendere la nostra piattaforma sempre più completa!
          </FormNote>
        </SuggestEventForm>
      )}
    </SuggestEventModal>
  </SuggestEventPopup>
)}


     
    </MapWrapper>
  );
};

export default EventMap;
